import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { get } from 'lodash';
import { landingPageReferralUrls } from 'client/GrowthEngineering/utilities/GrowthConstants';
import store from 'client/store/index';

const growthModule = StoreModuleMap[ItemTypeConsts.growth];

/**
 * A list of page view events. These events are specifically used to begin Optimizely experiments.
 */
export const pageViewEvents = {
  adminPortalConsole: 'adminPortalConsole',
  conditionalAccessPoliciesPage: 'conditionalAccessPoliciesPage',
  ldapPage: 'ldapPage',
  notMspNorCommandRunner: 'notMspNorCommandRunner',
  signup: 'signup_product',
  signupAdminPortalCrossover: 'signupAdminPortalCrossover',
  supportPage: 'adminPortalSupport',
  newSystemPage: 'newSystemPage',
  windowsAppModal: 'windowsAppModal',
};

/**
 * A list of Optimizely experiments.
 * @property {Number} experimentId - The id of the experiment. Returned in Optimizely's `state`
 * object.
 * @property {String} name - The name of the experiment. Returned in Optimizely's `state` object.
 * @property {String} pageViewEvent - The API name of the page view event that triggers the
 * experiment
 * @property {String} startDate - The date the experiment was started
 * @property {Boolean} showToOlderOrgs - Set to true to show the experiment to organizations
 * created before the experiment start date
 * @property {Object} variations - An array of the names of the possible variations a user may
 * be shown
 */
export const experiments = {
  landingPage: {
    experimentId: 20097032647,
    name: 'Landing Page',
    startDate: '2021/03/24',
    variations: {
      user: 'User Page',
      home: 'Home Page',
    },
  },
  landingPageReferralOnboarding: {
    experimentId: 22737697254,
    name: 'Landing Page Referral Onboarding',
    startDate: '2023/01/13',
    variations: {
      original: 'Original',
      new: 'Landing Page Based Variation',
    },
    /**
     * Custom eligibility check for the Landing Page Referral experiment
     * @returns {Boolean} - Returns true for eligible admins
     */
    eligibilityCheck: (
      () => {
        const { orgGrowthData } = store.state[growthModule];
        const growthExperimentStates = orgGrowthData.experimentStates;
        const { experimentId } = experiments.landingPageReferralOnboarding;
        const state = get(growthExperimentStates, experimentId, {});
        const signupReferralUrl = get(state, 'signupReferralUrl', '');

        return (signupReferralUrl.startsWith(landingPageReferralUrls.cloudDirectory)
            || signupReferralUrl.startsWith(landingPageReferralUrls.crossOsDeviceManagement));
      }
    ),
  },
  optimizelyAudienceTester: {
    experimentId: 20236109620,
    name: 'Optimizely Audience Tester',
    pageViewEvent: pageViewEvents.notMspNorCommandRunner,
    startDate: '2000-01-01',
    variations: {
      original: 'Original',
      new: 'In Audience',
    },
  },
  recommendPackageInChecklist: {
    experimentId: 21068300477,
    name: 'Recommended Package in Implementation Checklist',
    pageViewEvent: pageViewEvents.notMspNorCommandRunner,
    startDate: '2022-01-14',
    showToOlderOrgs: true,
    variations: {
      original: 'Original',
      new: 'Show Recommended Package',
    },
  },
  ssoNavBarRenaming: {
    experimentId: 22640402990,
    name: 'Nav bar SSO',
    pageViewEvent: pageViewEvents.notMspNorCommandRunner,
    startDate: '2023-01-17',
    showToOlderOrgs: false,
    variations: {
      original: 'Original',
      new: 'Applications',
    },
  },
};

export default {
  /**
   * This snippet is taken from Optimizely's documentation and is used to load Optimizely
   * @see https://support.optimizely.com/hc/en-us/articles/4410289790221-Install-the-Optimizely-snippet-as-a-non-blocking-resource
   */
  loadOptimizely() {
    const loadScript = document.createElement('script');
    loadScript.src = 'https://thirdparty.jumpcloud.com/js/optimizely-prd.min.js';
    document.head.appendChild(loadScript);
  },

  initialize() {
    this.optimizely = window.optimizely || [];
  },

  /**
   * Retrieves which variation a user is being shown for a given experiment
   * @param {Object} experiment - An experiment object taken from the list of experiments defined
   * @param {Object} [optimizelyState] - The object returned from optimizely.get('state')
   */
  getVariation(experiment, optimizelyState) {
    if (!this.optimizely || !this.optimizely.get) return '';

    const state = optimizelyState || this.optimizely.get('state');
    const experimentStates = state.getExperimentStates({
      isActive: true,
    });
    const values = Object.values(experimentStates);
    const experimentObj = values.find(e => e.experimentName === experiment.name);

    if (!experimentObj || !experimentObj.isActive) {
      // Experiment was not found or
      // Experiment is not active
      return '';
    }

    const variation = get(experimentObj, 'variation.name', '');
    // Checks if experiment should be added to org GrowthData object
    const { orgGrowthData } = store.state[growthModule];
    const growthExperimentStates = orgGrowthData.experimentStates;
    const isAdmin = store.getters['SettingsModule/isAdmin'];

    if (variation && !growthExperimentStates[experimentObj.id] && isAdmin) {
      store.dispatch(`${growthModule}/addExperimentToOrgGrowthData`, experimentObj);
    }

    return variation;
  },

  /**
   * Retrieves whether a given experiment is active for a user
   * @param {Object} experiment - An experiment object
   * @param {Object} [optimizelyState] - The object returned from optimizely.get('state')
   */
  getIsActive(experiment, optimizelyState) {
    if (!this.optimizely || !this.optimizely.get) return '';

    const state = optimizelyState || this.optimizely.get('state');
    const experimentStates = state.getExperimentStates();
    const values = Object.keys(experimentStates).map(e => experimentStates[e]);

    // Name could be experiment.name or experiment.experimentName if experiment
    // is passed from Optimizely
    const experimentName = experiment.name || experiment.experimentName;
    const experimentObj = values.find(e => e.experimentName === experimentName);

    if (!experimentObj) {
      // Experiment was not found
      return false;
    }

    return experimentObj.isActive;
  },

  /**
   * Activates a specific experiment in Optimizely
   * @see https://developers.optimizely.com/x/solutions/javascript/reference/index.html#function_setpage
   * @param {Object} experiment - An experiment object taken from the list of experiments defined
   */
  trackPageViewEvent(pageViewEvent) {
    if (!this.optimizely || !this.optimizely.push) return;

    this.optimizely.push({
      type: 'page',
      pageName: pageViewEvent,
    });
  },

  /**
   * Adds custom attributes to use for audience targeting in Optimizely
   * @see https://docs.developers.optimizely.com/web/docs/user
   * @param {Object} attributes - The custom attributes to set for the user
   */
  setCustomAttributes(attributes) {
    if (!this.optimizely || !this.optimizely.push) return;

    this.optimizely.push({
      type: 'user',
      attributes,
    });
  },
};
